/**
 * Canvas utility functions for handling DPI and high-quality rendering
 */

/**
 * Setup canvas with proper DPI handling for Retina/HiDPI displays
 *
 * @param canvas - HTMLCanvasElement to configure
 * @param displayWidth - CSS display width in logical pixels
 * @param displayHeight - CSS display height in logical pixels
 * @returns Actual canvas dimensions (considering DPI)
 *
 * @example
 * const canvas = document.createElement('canvas');
 * const dims = setupCanvasDPI(canvas, 540, 960);
 * console.log(dims); // { width: 1080, height: 1920 } on 2x DPI screen
 */
export function setupCanvasDPI(
  canvas: HTMLCanvasElement,
  displayWidth: number,
  displayHeight: number
): { width: number; height: number } {
  const dpr = window.devicePixelRatio || 1;

  // Set actual canvas pixel dimensions (considering DPI)
  canvas.width = Math.round(displayWidth * dpr);
  canvas.height = Math.round(displayHeight * dpr);

  // Set CSS display dimensions
  canvas.style.width = `${displayWidth}px`;
  canvas.style.height = `${displayHeight}px`;

  return {
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Get recommended composition canvas size based on display canvas
 *
 * @param displayCanvas - The preview canvas element
 * @returns Recommended composition dimensions
 *
 * @example
 * const canvas = document.getElementById('preview');
 * const composeSize = getCompositionSize(canvas);
 * // Use composeSize.width and composeSize.height for Meframe config
 */
export function getCompositionSize(displayCanvas: HTMLCanvasElement): {
  width: number;
  height: number;
} {
  return {
    width: displayCanvas.width,
    height: displayCanvas.height,
  };
}

/**
 * Create a high-DPI canvas with proper settings
 *
 * @param container - Optional container element to append canvas to
 * @param options - Canvas configuration options
 * @returns Configured canvas element and its dimensions
 *
 * @example
 * const { canvas, dimensions } = createHiDPICanvas(document.body, {
 *   displayWidth: 540,
 *   displayHeight: 960,
 * });
 *
 * const meframe = await Meframe.create({
 *   canvas,
 *   config: {
 *     compose: {
 *       canvas: {
 *         width: dimensions.width,
 *         height: dimensions.height,
 *       }
 *     }
 *   }
 * });
 */
export function createHiDPICanvas(
  container?: HTMLElement,
  options: {
    displayWidth: number;
    displayHeight: number;
    className?: string;
    id?: string;
  } = { displayWidth: 540, displayHeight: 960 }
): {
  canvas: HTMLCanvasElement;
  dimensions: { width: number; height: number };
} {
  const canvas = document.createElement('canvas');

  if (options.id) {
    canvas.id = options.id;
  }

  if (options.className) {
    canvas.className = options.className;
  }

  const dimensions = setupCanvasDPI(canvas, options.displayWidth, options.displayHeight);

  if (container) {
    container.appendChild(canvas);
  }

  return { canvas, dimensions };
}

/**
 * Check if canvas DPI is properly configured
 *
 * @param canvas - Canvas element to check
 * @returns Diagnostic information about canvas DPI setup
 *
 * @example
 * const info = checkCanvasDPI(myCanvas);
 * if (!info.isOptimal) {
 *   console.warn('Canvas DPI not optimal:', info.message);
 * }
 */
export function checkCanvasDPI(canvas: HTMLCanvasElement): {
  dpr: number;
  canvasWidth: number;
  canvasHeight: number;
  displayWidth: number;
  displayHeight: number;
  expectedWidth: number;
  expectedHeight: number;
  isOptimal: boolean;
  message: string;
} {
  const dpr = window.devicePixelRatio || 1;
  const displayWidth = canvas.clientWidth;
  const displayHeight = canvas.clientHeight;
  const expectedWidth = Math.round(displayWidth * dpr);
  const expectedHeight = Math.round(displayHeight * dpr);

  const widthMatch = Math.abs(canvas.width - expectedWidth) <= 1;
  const heightMatch = Math.abs(canvas.height - expectedHeight) <= 1;
  const isOptimal = widthMatch && heightMatch;

  let message = '';
  if (!isOptimal) {
    message = `Canvas DPI mismatch! Expected ${expectedWidth}×${expectedHeight} but got ${canvas.width}×${canvas.height}. This will cause blurry rendering on ${dpr}x DPI screens.`;
  } else {
    message = `Canvas DPI is properly configured for ${dpr}x display.`;
  }

  return {
    dpr,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    displayWidth,
    displayHeight,
    expectedWidth,
    expectedHeight,
    isOptimal,
    message,
  };
}
